/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import StateViewer from "App/state_viewer";
import WebGLContext from "App/web_gl_context";
import MemoryManagement from "App/memory_management";
import Shape from "App/geometry/objects/shape";
import RenderDocument from "App/render_document";
import IScene from "js_interface/src/scene_interface";

export default
class ModelStatistics {

    private _sceneAPI: IScene;
    private _renderDocument: RenderDocument;
    private _stateViewer: StateViewer;
    private _statistics: any = {};
    private _textureSwitchStats: any = {
        allSwitchRequestsCount:     0,
        visibleSwitchRequestsCount: 0
    };

    private _updateInfoWindowInterval: number = 0;
    private _fps: number = 0;
    private _dirty: boolean = true;

    constructor(aRenderDocument: RenderDocument) {
        this._renderDocument = aRenderDocument;
        this._sceneAPI = aRenderDocument.getSceneAPI();
        this._stateViewer = this._sceneAPI.getStateViewer();

        this._clearStatistics();
    }

    // code from http://stackoverflow.com/questions/15900485/correct-way-to-convert-size-in-bytes-to-kb-mb-gb-in-javascript
    private bytesToSize(bytes: number): string {
        if (bytes === 0) {
            return '0 Byte';
        }
        let k = 1000;
        let sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        let i = Math.floor(Math.log(bytes) / Math.log(k));
        return (bytes / Math.pow(k, i)).toPrecision(3) + ' ' + sizes[i];
    }

    public enable(): void
    {
        if (this._updateInfoWindowInterval) {
            return;
        }

        this._updateInfoWindowInterval = window.setInterval(() => {
            if (this._dirty) {
                this._clearStatistics();
                this._renderDocument.getRenderer().countShapesAndUniqueTextures(this);
                this._updateInfoWindow();
                this._dirty = false;
            }
        }, 1000);
    }

    public disable(): void
    {
        if (!this._updateInfoWindowInterval) {
            return;
        }

        clearInterval(this._updateInfoWindowInterval);
        this._updateInfoWindowInterval = 0;
    }

    public setTextureSwitchStatistics(aVisibleCount: number, aAllCount: number): void
    {
        this._textureSwitchStats.allSwitchRequestsCount     = aAllCount;
        this._textureSwitchStats.visibleSwitchRequestsCount = aVisibleCount;

        this.setDirty();
    }


    /**
     * Add geometry of the aShape reference to the model statistics. The geometry of the shape have to be already available
     * so this function should be called when the geometry data arrive
     *
     * @param {object} aShape reference to the shape with valid geometry
     */
    public addShape(aShape: Shape): void
    {
        // count the shape types;
        switch (aShape.getType()) {
            case "POI":
                this._statistics.POIs++;
                break;
            case "POINT":
                this._statistics.points++;
                break;
            case "LINE":
                this._statistics.lines++;
                break;
            case "OUTLINE":
                this._statistics.outlines++;
                break;
            case "TEXT":
                this._statistics.texts++;
                break;
            case "POINTCLOUD":
                this._statistics.pointClouds++;
                break;
            case "SHAPE":
                if (aShape.isOpaque()) {
                    this._statistics.shapes++;
                }
                else {
                    this._statistics.transparents++;
                }
                break;
        }

        this._statistics.vertices += aShape.getVerticesCount();
        this._statistics.faces += aShape.getFacesCount();
    }

    public addTextureNode(): void
    {
        this._statistics.textureNodes++;
    }

    public addMaterialNode(): void
    {
        this._statistics.materialNodes++;
    }

    public addRenderOptionNode(): void
    {
        this._statistics.renderOptionNodes++;
    }

    public addShapeBatchNode(): void
    {
        this._statistics.shapeBatchNodes++;
    }

    public addTexture(aIsLD: boolean): void
    {
        if (aIsLD) {
            this._statistics.ldTextures++;
        }
        else {
            this._statistics.textures++;
        }
    }

    public setDirty(): void
    {
        this._dirty = true;
    }

    public setFPS(aValue: number): void
    {
        this._fps = aValue;
        this._stateViewer.addFixedMeasurement('FPS', aValue);
    }

    private _clearStatistics(): void
    {
        this._statistics = {
            shapes: 0,
            transparents: 0,
            points: 0,
            pointClouds: 0,
            lines: 0,
            outlines: 0,
            texts: 0,
            POIs: 0,
            textures: 0,
            ldTextures: 0,
            vertices: 0,
            faces: 0,
            allSwitchRequests: 0,
            visibleSwitchRequests: 0,
            textureLodDebugEnabled: false,
            geometryLodDebugEnabled: false,
            textureResolutionLimit: 0,
            GPUGeometrySize: 0,
            GPUScreeBufferSize: 0,
            GPURenderBufferSize: 0,
            GPUTexturesSize: 0,
            textureNodes: 0,
            materialNodes: 0,
            renderOptionNodes: 0,
            shapeBatchNodes: 0,
            fps: 0
        };
    }

    private _updateRealtimeStats(): void
    {
        this._statistics.allSwitchRequests = this._textureSwitchStats.allSwitchRequestsCount;
        this._statistics.visibleSwitchRequests = this._textureSwitchStats.visibleSwitchRequestsCount;
        this._statistics.textureLodDebugEnabled = this._sceneAPI.renderSettingsModule.getTextureLODDebugEnabled();
        this._statistics.geometryLodDebugEnabled = this._sceneAPI.renderSettingsModule.getGeometryLODDebugEnabled();
        this._statistics.textureResolutionLimit = this._sceneAPI.renderSettingsModule.getTexturesResolutionLimit();

        let occupiedGPUMemory = MemoryManagement.getOccupiedGPUMemory();
        let htmlCanvas = this._sceneAPI.getHTMLCanvas();
        let screenMem = htmlCanvas.clientWidth * htmlCanvas.clientHeight * 4 * 2 * this._sceneAPI.renderSettingsModule.getQualityPerformanceFactor();

        this._statistics.GPUGeometrySize = occupiedGPUMemory.geometry;
        this._statistics.GPUScreeBufferSize = screenMem * ((WebGLContext.caps.SAMPLES) ? WebGLContext.caps.SAMPLES : 1);
        this._statistics.GPURenderBufferSize = occupiedGPUMemory.renderBuffers;
        this._statistics.GPUTexturesSize = occupiedGPUMemory.textures;

        this._statistics.fps = this._renderDocument.getFps();
    }

    public getStatistics(): any
    {
        // update to current values and return
        if (this._dirty) {
            this._clearStatistics();
            this._renderDocument.getRenderer().countShapesAndUniqueTextures(this);
            this._updateInfoWindow();
            this._dirty = false;
        }

        this._updateRealtimeStats();

        // copy statistics object
        let retStats: any = {};
        for (let key in this._statistics) {
            retStats[key] = this._statistics[key];
        }

        // postprocess the statistics values a bit
        retStats.vertices = parseInt(retStats.vertices).toLocaleString();
        retStats.faces = parseInt(retStats.faces).toLocaleString();

        retStats.GPUGeometrySize = this.bytesToSize(retStats.GPUGeometrySize);
        retStats.GPUScreeBufferSize = this.bytesToSize(retStats.GPUScreeBufferSize);
        retStats.GPURenderBufferSize = this.bytesToSize(retStats.GPURenderBufferSize);
        retStats.GPUTexturesSize = this.bytesToSize(retStats.GPUTexturesSize);

        let ldTexColors = StateViewer.getLDTexturesColorLabels();
        retStats.LDTExturesColors = {
            "256": ldTexColors["256"],
            "512": ldTexColors["512"],
            "1024": ldTexColors["1024"],
            "2048": ldTexColors["2048"]
        };

        let glodColors = StateViewer.getGeometryLODColorLabels();
        retStats.GLODColors = {
            "0": glodColors["0"],
            "1": glodColors["1"],
            "2": glodColors["2"],
            "3": glodColors["3"],
            "4": glodColors["4"],
            "5": glodColors["5"]
        };


        return retStats;
    }

    public _updateInfoWindow = function()
    {
        this._stateViewer.addSection("--- MODEL STATS ---");
        this._stateViewer.addMeasurement("#vertices", parseInt(this._statistics.vertices).toLocaleString());
        this._stateViewer.addMeasurement("#faces", parseInt(this._statistics.faces).toLocaleString());
        this._stateViewer.addMeasurement("#shapes", this._statistics.shapes);
        this._stateViewer.addMeasurement("#transparents", this._statistics.transparents);
        this._stateViewer.addMeasurement("#points", this._statistics.points);
        this._stateViewer.addMeasurement("#pointClouds", this._statistics.pointClouds);
        this._stateViewer.addMeasurement("#lines", this._statistics.lines);
        this._stateViewer.addMeasurement("#outlines", this._statistics.outlines);
        this._stateViewer.addMeasurement("#texts", this._statistics.texts);
        this._stateViewer.addMeasurement("#POIs", this._statistics.POIs);
        this._stateViewer.addMeasurement("#textures", this._statistics.textures);
        this._stateViewer.addMeasurement("#ldTextures", this._statistics.ldTextures);

        this._stateViewer.addSection("--- GRAPH NODES COUNT ---");

        this._stateViewer.addMeasurement("#texture", this._statistics.textureNodes);
        this._stateViewer.addMeasurement("#material", this._statistics.materialNodes);
        this._stateViewer.addMeasurement("#render option", this._statistics.renderOptionNodes);
        this._stateViewer.addMeasurement("#shape batches", this._statistics.shapeBatchNodes);

        this._updateRealtimeStats();

        this._stateViewer.addSection("--- LD TEXTURE INFO ---");
        this._stateViewer.addMeasurement("#all switches:",  this._statistics.allSwitchRequests);
        this._stateViewer.addMeasurement("#visible switches:",  this._statistics.visibleSwitchRequests);
        let self = this;
        this._stateViewer.addCheckBox("#Color textures: ", function(aCheckbox) {
            self._sceneAPI.renderSettingsModule.setTextureLODDebugEnabled(aCheckbox.checked);
        });

        this._stateViewer.addLDTexturesLabel();

        this._stateViewer.addCheckBox("#Color geometry: ", function(aCheckbox) {
            self._sceneAPI.renderSettingsModule.setGeometryLODDebugEnabled(aCheckbox.checked);
        });

        this._stateViewer.addGLODLabel();

        if (this._statistics.textureResolutionLimit !== -1) {
            this._stateViewer.addMeasurement("#Resolution limit:", this._statistics.textureResolutionLimit);
        }

        this._stateViewer.addSection("--- GPU occ. memory ---");
        this._stateViewer.addMeasurement("#geometry:", this.bytesToSize(this._statistics.GPUGeometrySize), false);

        this._stateViewer.addMeasurement("#screen:",      this.bytesToSize(this._statistics.GPUScreeBufferSize), false);
        this._stateViewer.addMeasurement("#render buff:", this.bytesToSize(this._statistics.GPURenderBufferSize), false);
        this._stateViewer.addMeasurement("#textures:",    this.bytesToSize(this._statistics.GPUTexturesSize), false);
    }
}
